import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import React, { memo, useCallback, useMemo, useState } from "react";
import { KTIcon } from "../../../_metronic/helpers";
import ExportButton from "../../../utility/ExportButton";

interface DataTableProps<T> {
	data: T[];
	columns: any[];
	pageSizeOptions?: number[];
	defaultPageSize?: number;
	searchableColumn?: keyof T;
	isLoading?: boolean;
	error?: Error | null;
	className?: string;
	fileName?: string;
	customGlobalFilter?: string;
	setCustomGlobalFilter?: (value: string) => void;
	hideSearchInput?: boolean;
	hideExport?: boolean;
	totalCount?: number;
	manualPagination?: boolean;
	pageCount?: number;
	onPaginationChange?: (pagination: {
		pageIndex: number;
		pageSize: number;
	}) => void;
	currentPageIndex?: number;
	currentPageSize?: number;
	toolbarContent?: React.ReactNode;
}

const SearchInput = memo<{ value: string; onChange: (value: string) => void }>(
	({ value, onChange }) => {
		const [localValue, setLocalValue] = useState(value ?? "");

		React.useEffect(() => {
			setLocalValue(value ?? "");
		}, [value]);

		React.useEffect(() => {
			const handler = setTimeout(() => {
				if (localValue !== (value ?? "")) {
					onChange(localValue);
				}
			}, 300);
			return () => clearTimeout(handler);
		}, [localValue, value, onChange]);

		return (
			<div className="premium-search-wrapper">
				{/* <KTIcon iconName="magnifier" className="premium-search-icon" /> */}
				<input
					type="text"
					className="form-control premium-search-input"
					placeholder="Search anything..."
					value={localValue}
					onChange={(e) => setLocalValue(e.target.value)}
					aria-label="Search table data"
				/>
			</div>
		);
	},
);

export const ExpandableSearchInput = memo<{
	value: string;
	onChange: (value: string) => void;
}>(({ value, onChange }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const handleBlur = () => {
		if (!value) {
			setIsExpanded(false);
		}
	};

	return isExpanded || value ? (
		<div className="d-flex align-items-center gap-2 animate__animated animate__fadeInRight animate__faster">
			<div className="premium-search-wrapper" style={{ maxWidth: "250px" }}>
				<KTIcon iconName="magnifier" className="premium-search-icon fs-2" />
				<input
					type="text"
					className="form-control premium-search-input py-1 fs-7"
					placeholder="Search..."
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onBlur={handleBlur}
					autoFocus
				/>
				{value && (
					<button
						className="btn btn-icon btn-sm btn-active-light-primary position-absolute end-0 top-50 translate-middle-y me-1"
						style={{ height: "24px", width: "24px" }}
						onClick={() => {
							onChange("");
							setIsExpanded(false);
						}}
					>
						<i className="bi bi-x fs-4"></i>
					</button>
				)}
			</div>
		</div>
	) : (
		<button
			className="btn btn-icon btn-light-primary btn-active-primary w-35px h-35px rounded-circle shadow-sm"
			onClick={() => setIsExpanded(true)}
			title="Search"
		>
			<i className="bi bi-search fs-4"></i>
		</button>
	);
});

// Check if SearchInput is already exported above. authentic code shows `export const SearchInput`.
// I will just add ExpandableSearchInput after it.

const PageSizeSelector = memo<{
	pageSize: number;
	pageSizeOptions: number[];
	onPageSizeChange: (size: number) => void;
}>(({ pageSize, pageSizeOptions, onPageSizeChange }) => {
	return (
		<div className="premium-select-wrapper">
			<span className="premium-select-label">Show</span>
			<select
				className="form-select premium-select"
				value={pageSize}
				onChange={(e) => onPageSizeChange(Number(e.target.value))}
				aria-label="Select rows per page"
			>
				{pageSizeOptions.map((size) => (
					<option key={size} value={size}>
						{size}
					</option>
				))}
			</select>
			<span className="premium-select-label">Entries</span>
		</div>
	);
});

const PaginationControls = memo<{
	canPreviousPage: boolean;
	canNextPage: boolean;
	pageCount: number;
	pageIndex: number;
	setPageIndex: (index: number) => void;
	previousPage: () => void;
	nextPage: () => void;
}>(
	({
		canPreviousPage,
		canNextPage,
		pageCount,
		pageIndex,
		setPageIndex,
		previousPage,
		nextPage,
	}) => {
		const renderPageNumbers = () => {
			const pages: JSX.Element[] = [];
			const maxVisiblePages = 5;
			let startPage = Math.max(0, pageIndex - Math.floor(maxVisiblePages / 2));
			let endPage = Math.min(pageCount - 1, startPage + maxVisiblePages - 1);

			if (endPage - startPage + 1 < maxVisiblePages) {
				startPage = Math.max(0, endPage - maxVisiblePages + 1);
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(
					<li
						key={i}
						className={`page-item ${i === pageIndex ? "active" : ""}`}
					>
						<button className="page-link" onClick={() => setPageIndex(i)}>
							{i + 1}
						</button>
					</li>,
				);
			}
			return pages;
		};

		return (
			<nav aria-label="Table pagination">
				<ul className="pagination premium-pagination mb-0">
					<li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={() => setPageIndex(0)}
							disabled={!canPreviousPage}
							aria-label="First page"
						>
							<i className="bi bi-chevron-double-left"></i>
						</button>
					</li>
					<li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={previousPage}
							disabled={!canPreviousPage}
							aria-label="Previous page"
						>
							<i className="bi bi-chevron-left"></i>
						</button>
					</li>

					{renderPageNumbers()}

					<li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={nextPage}
							disabled={!canNextPage}
							aria-label="Next page"
						>
							<i className="bi bi-chevron-right"></i>
						</button>
					</li>
					<li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={() => setPageIndex(pageCount - 1)}
							disabled={!canNextPage}
							aria-label="Last page"
						>
							<i className="bi bi-chevron-double-right"></i>
						</button>
					</li>
				</ul>
			</nav>
		);
	},
);

export function DataTable<T extends object>({
	data,
	columns,
	pageSizeOptions = [5, 10, 20, 50],
	defaultPageSize = 10,
	isLoading = false,
	error = null,
	fileName,
	className = "",
	customGlobalFilter,
	setCustomGlobalFilter,
	hideSearchInput = false,
	hideExport = false,
	totalCount,
	manualPagination = false,
	pageCount: externalPageCount,
	onPaginationChange: onExternalPaginationChange,
	currentPageIndex,
	currentPageSize,
	toolbarContent,
}: DataTableProps<T>) {
	// Manage pagination state
	const [localPageIndex, setLocalPageIndex] = useState(0);
	const [localPageSize, setLocalPageSize] = useState(defaultPageSize);
	const [localGlobalFilter, setLocalGlobalFilter] = useState("");

	const pageIndex = currentPageIndex ?? localPageIndex;
	const pageSize = currentPageSize ?? localPageSize;

	const globalFilter = customGlobalFilter ?? localGlobalFilter;
	const setGlobalFilter = setCustomGlobalFilter ?? setLocalGlobalFilter;

	const handlePaginationChange = useCallback(
		(updater: any) => {
			const newState =
				typeof updater === "function"
					? updater({ pageIndex, pageSize })
					: updater;

			if (onExternalPaginationChange) {
				onExternalPaginationChange(newState);
			} else {
				setLocalPageIndex(newState.pageIndex);
				setLocalPageSize(newState.pageSize);
			}
		},
		[pageIndex, pageSize, onExternalPaginationChange],
	);

	const table = useReactTable({
		data: useMemo(() => data, [data]),
		columns: useMemo(() => columns, [columns]),
		state: {
			globalFilter,
			pagination: {
				pageIndex,
				pageSize,
			},
		},
		onGlobalFilterChange: setGlobalFilter,
		onPaginationChange: handlePaginationChange,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: manualPagination
			? undefined
			: getFilteredRowModel(),
		getPaginationRowModel: manualPagination
			? undefined
			: getPaginationRowModel(),
		manualPagination,
		manualFiltering: manualPagination,
		pageCount: externalPageCount,
		globalFilterFn: (row, columnId, filterValue: string) => {
			const rawValue = row.getValue(columnId);
			let valueString = "";

			if (rawValue instanceof Date) {
				valueString = dayjs(rawValue).format("DD-MM-YYYY hh:mm A");
			} else if (
				typeof rawValue === "string" &&
				!Number.isNaN(Date.parse(rawValue))
			) {
				valueString = dayjs(rawValue).format("DD-MM-YYYY hh:mm A");
			} else {
				valueString = String(rawValue ?? "");
			}

			return valueString.toLowerCase().includes(filterValue.toLowerCase());
		},
	});

	const handlePageSizeChange = useCallback(
		(size: number) => {
			table.setPageSize(size);
		},
		[table],
	);

	return (
		<div className={`premium-table-wrapper ${className}`}>
			{error && (
				<div className="alert alert-danger" role="alert">
					{error.message || "An error occurred while loading the data"}
				</div>
			)}

			<div className="premium-table-controls d-flex flex-column flex-md-row justify-content-between align-items-center mb-0 mt-3 mx-3 gap-3">
				{!hideSearchInput && (
					<SearchInput
						value={globalFilter ?? ""}
						onChange={(value) => setGlobalFilter(value)}
					/>
				)}

				<div
					className={`d-flex gap-3 flex-wrap align-items-center justify-content-end ${hideSearchInput ? "w-100" : ""}`}
				>
					<div className="d-flex align-items-center gap-2 bg-light-primary px-4 py-2 rounded-pill border border-primary border-opacity-10">
						<i className="bi bi-layers text-primary fs-7"></i>
						<span
							className="text-primary fw-bold fs-8 text-uppercase"
							style={{ letterSpacing: "0.5px" }}
						>
							{totalCount ?? table.getPrePaginationRowModel().rows.length} Total
						</span>
					</div>

					<PageSizeSelector
						pageSize={pageSize}
						pageSizeOptions={pageSizeOptions}
						onPageSizeChange={handlePageSizeChange}
					/>
					{!hideExport && (
						<ExportButton data={data} columns={columns} sheetName="Sheet1" />
					)}
					{toolbarContent && (
						<div className="d-flex gap-2 ms-0 ms-md-3">{toolbarContent}</div>
					)}
				</div>
			</div>

			<div className="table-responsive px-3 pb-3">
				<table className="premium-table align-middle fs-5 gy-3 dataTable no-footer">
					<thead>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr
								key={headerGroup.id}
								className="text-center text-gray-700 fw-bolder fs-6 text-uppercase gs-0"
							>
								{headerGroup.headers.map((header) => {
									// tanstack-table already computes the correct rowSpan for
									// each header (accounting for grouped/multi-row headers)
									// and marks the cells that only exist to reserve column
									// space in shallower rows as placeholders — those render
									// as nothing, since the real header's rowSpan already
									// visually covers that space.
									if (header.isPlaceholder) return null;
									return (
										<th
											key={header.id}
											scope="col"
											colSpan={header.colSpan}
											rowSpan={header.rowSpan}
											className="text-nowrap"
											style={{
												cursor: header.column.getCanSort()
													? "pointer"
													: "default",
												textAlign:
													(
														header.column.columnDef.meta as
															| { align?: "left" | "center" | "right" }
															| undefined
													)?.align === "right"
														? "right"
														: (
																	header.column.columnDef.meta as
																		| { align?: "left" | "center" | "right" }
																		| undefined
																)?.align === "left"
															? "left"
															: "center",
											}}
											onClick={header.column.getToggleSortingHandler()}
											aria-sort={
												header.column.getIsSorted() === "asc"
													? "ascending"
													: header.column.getIsSorted() === "desc"
														? "descending"
														: "none"
											}
										>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
											{header.column.getCanSort() && (
												<span className="ms-2 opacity-50">
													{header.column.getIsSorted() === "asc" ? (
														<i className="bi bi-caret-up-fill text-primary"></i>
													) : header.column.getIsSorted() === "desc" ? (
														<i className="bi bi-caret-down-fill text-primary"></i>
													) : (
														<i className="bi bi-code-vertical fs-9"></i>
													)}
												</span>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={columns.length} className="text-center py-4">
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</td>
							</tr>
						) : error ? (
							<tr>
								<td
									colSpan={columns.length}
									className="text-center py-4 text-danger"
								>
									Failed to load data
								</td>
							</tr>
						) : table.getRowModel().rows.length === 0 ? (
							<tr>
								<td colSpan={columns.length} className="text-center py-4">
									No data available
								</td>
							</tr>
						) : (
							table.getRowModel().rows.map((row) => (
								<tr key={row.id}>
									{row.getVisibleCells().map((cell) => (
										<td
											key={cell.id}
											className="text-center"
											style={{
												textAlign:
													(
														cell.column.columnDef.meta as
															| { align?: "left" | "center" | "right" }
															| undefined
													)?.align === "right"
														? "right"
														: (
																	cell.column.columnDef.meta as
																		| { align?: "left" | "center" | "right" }
																		| undefined
																)?.align === "left"
															? "left"
															: "center",
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 mb-3 mx-4 gap-3">
				<div className="text-muted fw-bold fs-6">
					Showing{" "}
					{table.getRowModel().rows.length > 0 ? pageIndex * pageSize + 1 : 0}{" "}
					to{" "}
					{Math.min(
						(pageIndex + 1) * pageSize,
						totalCount ?? table.getPrePaginationRowModel().rows.length,
					)}{" "}
					of {totalCount ?? table.getPrePaginationRowModel().rows.length}{" "}
					records
				</div>

				<PaginationControls
					canPreviousPage={table.getCanPreviousPage()}
					canNextPage={table.getCanNextPage()}
					pageCount={table.getPageCount()}
					pageIndex={pageIndex}
					setPageIndex={table.setPageIndex}
					previousPage={table.previousPage}
					nextPage={table.nextPage}
				/>
			</div>
		</div>
	);
}

export default memo(DataTable);
